'use client';

import { Link } from 'react-router-dom';
import styles from './Cart.module.css';
import { products } from '@/data/products';
import { useCart } from '@/context/cart';
import { buildWhatsAppUrl, SALES_PHONE_READABLE, SUPPORT_PHONE_READABLE } from '@/utils/whatsapp';
import { PageSEO } from '@/seo/PageSEO';
import { IconTrash, IconWhatsApp, IconPhone } from '@/components/Icons/Icons';

function formatTRY(value: number) {
  try {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(value);
  } catch {
    return `${value} TL`;
  }
}

export function Cart() {
  const cart = useCart();
  const items = cart.lines
    .map((l) => ({ line: l, product: products.find((p) => p.slug === l.slug) }))
    .filter(
      (x): x is { line: { slug: string; qty: number }; product: (typeof products)[number] } =>
        Boolean(x.product)
    );

  const totals = items.reduce(
    (acc, x) => {
      acc.totalQty += x.line.qty;
      if (typeof x.product.priceTRY === 'number') acc.subtotalTRY += x.product.priceTRY * x.line.qty;
      else acc.hasUnknownPrice = true;
      return acc;
    },
    { totalQty: 0, subtotalTRY: 0, hasUnknownPrice: false }
  );

  const messageLines = items.map((x) => `- ${x.product.name} x ${x.line.qty} (/${x.product.slug})`);
  const msg =
    `Merhaba, aşağıdaki ürünler için fiyat/teslimat bilgisi rica ediyorum:\n` +
    messageLines.join('\n') +
    '\n\nİletişim: ';

  const waUrl = buildWhatsAppUrl(msg);
  return (
    <main className={styles.wrap}>
      <PageSEO
        title="Sepet | Bondex Sünger"
        description="Bondex sünger ve Bariyerli Bondex ürünlerinizi sepete ekleyin ve siparişi WhatsApp üzerinden tamamlayın. Hızlı teklif ve teslimat bilgisi için iletişime geçin."
        pathname="/sepet"
        noIndex
      />

      <div className="container">
        <h1 className={styles.pageTitle}>Sepetim</h1>
        <div className={styles.grid}>
          <section className={styles.table} aria-label="Sepet ürünleri">
            {items.length === 0 ? (
              <div className={styles.empty}>
                <h2 className={styles.emptyTitle}>Sepetiniz boş</h2>
                <p className={styles.emptyText}>Ürün eklemek için ürünler sayfasına geçin.</p>
                <Link className={styles.emptyBtn} to="/urunler">
                  Ürünleri görüntüle
                </Link>
              </div>
            ) : (
              <>
                <div className={styles.tableHead}>
                  <span>Ürün</span>
                  <span className={styles.thRight}>Adet</span>
                  <span className={styles.thRight}>Birim Fiyat</span>
                  <span className={styles.thRight}>Tutar</span>
                  <span className={styles.thRight}></span>
                </div>
                <div className={styles.itemsList}>
                  {items.map((x) => (
                    <div key={x.product.slug} className={styles.line}>
                      <div className={styles.prod}>
                        <img className={styles.thumb} src={x.product.imagePath} alt={x.product.name} loading="lazy" />
                        <div className={styles.prodInfo}>
                          <h3 className={styles.prodName}>
                            <Link to={`/${x.product.slug}`}>{x.product.name}</Link>
                          </h3>
                          <Link className={styles.prodLink} to={`/${x.product.slug}`}>
                            Ürüne git
                          </Link>
                        </div>
                      </div>

                      <div className={styles.qtyWrap} aria-label={`${x.product.name} adet`}>
                        <button className={styles.qtyBtn} type="button" onClick={() => cart.setQty(x.product.slug, x.line.qty - 1)}>
                          −
                        </button>
                        <input
                          className={styles.qty}
                          type="number"
                          min={1}
                          value={x.line.qty}
                          onChange={(e) => cart.setQty(x.product.slug, Number(e.target.value))}
                          aria-label={`${x.product.name} adet`}
                        />
                        <button className={styles.qtyBtn} type="button" onClick={() => cart.setQty(x.product.slug, x.line.qty + 1)}>
                          +
                        </button>
                      </div>

                      <div className={styles.unitPrice}>
                        {typeof x.product.priceTRY === 'number' ? formatTRY(x.product.priceTRY) : 'Teklif'}
                      </div>

                      <div className={styles.price}>
                        {typeof x.product.priceTRY === 'number' ? formatTRY(x.product.priceTRY * x.line.qty) : 'Teklif'}
                      </div>

                      <button className={styles.removeBtn} onClick={() => cart.remove(x.product.slug)} type="button" aria-label="Ürünü kaldır">
                        <IconTrash />
                      </button>
                    </div>
                  ))}
                </div>
                <div className={styles.footerActions}>
                  <div className={styles.cartTotal}>
                    Toplam Tutar:{' '}
                    <strong>
                      {totals.subtotalTRY > 0 ? formatTRY(totals.subtotalTRY) : totals.hasUnknownPrice ? 'Teklif' : '0,00 TL'}
                    </strong>
                  </div>
                  <Link className={styles.continueLink} to="/urunler">
                    Alışverişe devam et
                  </Link>
                  <button className={styles.clearBtn} onClick={() => cart.clear()} type="button">
                    Sepeti tamamen boşalt
                  </button>
                </div>
              </>
            )}
          </section>

          <aside className={styles.sidebar}>
            <div className={styles.sidebarHeader}>
              <h2 className={styles.h2}>Siparişi Tamamla</h2>
              <p className={styles.sidebarText}>
                Sepetinizi WhatsApp ile iletin; ekip size fiyat ve teslimat bilgisiyle dönüş yapsın.
              </p>
            </div>

            <div className={styles.actions}>
              <a
                className={`btn btnPrimary ${styles.primaryCta}`}
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Siparişi WhatsApp'tan tamamla"
              >
                <IconWhatsApp />
                WhatsApp'tan Sipariş Ver
              </a>
              <a className={`btn ${styles.phoneBtn}`} href={`tel:${SALES_PHONE_READABLE.replace(/\s/g, '')}`}>
                <IconPhone />
                Hemen Ara: {SALES_PHONE_READABLE}
              </a>
              <a className={`btn ${styles.phoneBtn}`} href={`tel:${SUPPORT_PHONE_READABLE.replace(/\s/g, '')}`}>
                <IconPhone />
                Müşteri Hizmetleri: {SUPPORT_PHONE_READABLE}
              </a>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
